/* eslint-disable no-param-reassign */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export function useResizableColumns(columnIds, tableKey, options = {}) {
  const minWidth = options.minWidth ?? 90;
  const storageKey = `resizable-columns:${tableKey}`;
  const [widths, setWidths] = useState(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      return raw ? JSON.parse(raw) : {};
    } catch (_) {
      return {};
    }
  });

  const draggingRef = useRef({ active: false, colId: null, startX: 0, startWidth: 0 });

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(widths));
    } catch (_) {
    }
  }, [storageKey, widths]);

  const startDragging = useCallback((e, colId) => {
    e.preventDefault();
    e.stopPropagation();
    const targetTh = e.currentTarget?.parentElement;
    const startWidth = targetTh?.offsetWidth ?? widths[colId] ?? 0;
    draggingRef.current = {
      active: true,
      colId,
      startX: e.pageX,
      startWidth,
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [widths]);

  const autoFitColumn = useCallback((colId, resizerEl) => {
    try {
      const th = resizerEl?.parentElement;
      if (!th) return;
      const headerRow = th.parentElement;
      if (!headerRow) return;
      const table = th.closest('table');
      if (!table) return;
      const thList = Array.from(headerRow.children);
      const colIndex = thList.indexOf(th);
      if (colIndex < 0) return;

      let maxContentWidth = th.scrollWidth;
      const tbodyRows = table.querySelectorAll('tbody tr');
      tbodyRows.forEach((row) => {
        const cell = row.children[colIndex];
        if (cell) {
          maxContentWidth = Math.max(maxContentWidth, cell.scrollWidth);
        }
      });

      const safetyPadding = 16;
      const computedWidth = Math.max(minWidth, Math.round(maxContentWidth + safetyPadding));
      setWidths((prev) => ({ ...prev, [colId]: computedWidth }));
    } catch (_) {
    }
  }, [minWidth, setWidths]);

  const onMouseMove = useCallback((e) => {
    const drag = draggingRef.current;
    if (!drag.active || !drag.colId) return;
    const delta = e.pageX - drag.startX;
    const newWidth = Math.max(minWidth, Math.round((drag.startWidth || minWidth) + delta));
    setWidths((prev) => ({ ...prev, [drag.colId]: newWidth }));
  }, [minWidth]);

  const stopDragging = useCallback(() => {
    if (!draggingRef.current.active) return;
    draggingRef.current = { active: false, colId: null, startX: 0, startWidth: 0 };
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  useEffect(() => {
    const onUp = () => stopDragging();
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('mouseleave', onUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('mouseleave', onUp);
    };
  }, [onMouseMove, stopDragging]);

  const resizableSet = useMemo(() => new Set(columnIds || []), [columnIds]);

  const getHeaderProps = useCallback((colId, { disabled } = {}) => {
    if (!resizableSet.has(colId) || disabled) return { style: {} };
    const px = widths[colId];
    return {
      style: px ? { width: `${px}px`, maxWidth: `${px}px` } : {},
    };
  }, [resizableSet, widths]);

  const getResizerFor = useCallback((colId, { disabled } = {}) => {
    if (!resizableSet.has(colId) || disabled) return null;
    return (
      <div
        className="col-resizer"
        onMouseDown={(e) => startDragging(e, colId)}
        onDoubleClick={(e) => autoFitColumn(colId, e.currentTarget)}
        role="separator"
        aria-orientation="vertical"
        aria-label={`Resize column ${colId}`}
      />
    );
  }, [resizableSet, startDragging, autoFitColumn]);

  return { widths, setWidths, getHeaderProps, getResizerFor };
}

export default useResizableColumns;



